/**
 * Collaborative yearly Memories — Game long-press create flow.
 */

import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { createMemoryInviteNotifications } from '../users/userNotifications';

export const MEMORIES_COLLECTION = 'memories';
export const MEMORY_TYPES = Object.freeze(['photo', 'video', 'journal']);
export const MEMORY_OPEN_MS = 24 * 60 * 60 * 1000;

const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm']);
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const VIDEO_MAX_BYTES = 25 * 1024 * 1024;

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function extForContentType(contentType, type) {
  if (type === 'photo') {
    if (contentType === 'image/png') return 'png';
    if (contentType === 'image/webp') return 'webp';
    return 'jpg';
  }
  if (contentType === 'video/webm') return 'webm';
  return 'mp4';
}

export function normalizeMemoryDoc(id, data = {}) {
  return {
    id,
    authorUid: data.authorUid || '',
    title: typeof data.title === 'string' ? data.title : '',
    memberUids: Array.isArray(data.memberUids) ? data.memberUids.filter(Boolean) : [],
    anniversaryMonth: Number(data.anniversaryMonth) || 1,
    anniversaryDay: Number(data.anniversaryDay) || 1,
    coverThumbUrl: data.coverThumbUrl || null,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
}

export function normalizeContributionDoc(id, data = {}) {
  return {
    id,
    authorUid: data.authorUid || '',
    type: MEMORY_TYPES.includes(data.type) ? data.type : 'journal',
    text: typeof data.text === 'string' ? data.text : '',
    mediaUrl: data.mediaUrl || null,
    mediaPath: data.mediaPath || null,
    mediaContentType: data.mediaContentType || null,
    mediaThumbUrl: data.mediaThumbUrl || null,
    mediaThumbPath: data.mediaThumbPath || null,
    createdAt: data.createdAt || null,
  };
}

/**
 * Open for first 24h after create, or on anniversary calendar day (local TZ).
 */
export function isMemoryOpen(memory, now = new Date()) {
  if (!memory) return false;
  const created = toDate(memory.createdAt);
  if (created && now.getTime() - created.getTime() < MEMORY_OPEN_MS) {
    return true;
  }
  const month = now.getMonth() + 1;
  const day = now.getDate();
  return (
    Number(memory.anniversaryMonth) === month && Number(memory.anniversaryDay) === day
  );
}

/** Next calendar open date label (local). */
export function nextMemoryOpenLabel(memory, now = new Date()) {
  if (!memory) return '';
  if (isMemoryOpen(memory, now)) return 'Open today';
  const y = now.getFullYear();
  let year = y;
  const candidate = new Date(y, (memory.anniversaryMonth || 1) - 1, memory.anniversaryDay || 1);
  if (candidate < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
    year = y + 1;
  }
  const d = new Date(year, (memory.anniversaryMonth || 1) - 1, memory.anniversaryDay || 1);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function memorySharePath(memoryId) {
  return `/memory/${memoryId}`;
}

async function uploadMemoryMedia({ uid, memoryId, contributionId, type, file, thumbFile }) {
  if (!file) throw new Error(type === 'photo' ? 'Choose a photo.' : 'Choose a video.');
  const allowed = type === 'photo' ? PHOTO_TYPES : VIDEO_TYPES;
  const max = type === 'photo' ? PHOTO_MAX_BYTES : VIDEO_MAX_BYTES;
  if (!allowed.has(file.type)) {
    throw new Error(
      type === 'photo' ? 'Photo must be JPEG, PNG, or WebP.' : 'Video must be MP4 or WebM.'
    );
  }
  if (file.size > max) {
    throw new Error(type === 'photo' ? 'Photo must be under 5MB.' : 'Video must be under 25MB.');
  }
  const ext = extForContentType(file.type, type);
  const mediaPath = `memories/${uid}/${memoryId}-${contributionId}.${ext}`;
  const storageRef = ref(storage, mediaPath);
  await uploadBytes(storageRef, file, { contentType: file.type });
  const mediaUrl = await getDownloadURL(storageRef);
  let mediaThumbUrl = null;
  let mediaThumbPath = null;
  if (thumbFile) {
    try {
      mediaThumbPath = `memories/${uid}/${memoryId}-${contributionId}-thumb.jpg`;
      const thumbRef = ref(storage, mediaThumbPath);
      await uploadBytes(thumbRef, thumbFile, { contentType: 'image/jpeg' });
      mediaThumbUrl = await getDownloadURL(thumbRef);
    } catch (err) {
      console.warn('memory thumb upload failed:', err);
    }
  }
  return {
    mediaUrl,
    mediaPath,
    mediaContentType: file.type,
    mediaThumbUrl,
    mediaThumbPath,
  };
}

/**
 * Create memory + first contribution. memberUids includes author + invitees.
 */
export async function createMemory({
  uid,
  title = '',
  type = 'photo',
  text = '',
  file = null,
  thumbFile = null,
  inviteUids = [],
}) {
  if (!uid) throw new Error('Sign in to create a memory.');
  if (!MEMORY_TYPES.includes(type)) throw new Error('Invalid memory type.');
  const now = new Date();
  const memoryRef = doc(collection(db, MEMORIES_COLLECTION));
  const contributionRef = doc(collection(db, MEMORIES_COLLECTION, memoryRef.id, 'contributions'));
  const members = [...new Set([uid, ...(inviteUids || []).filter(Boolean)])];

  let media = {
    mediaUrl: null,
    mediaPath: null,
    mediaContentType: null,
    mediaThumbUrl: null,
    mediaThumbPath: null,
  };
  if (type === 'photo' || type === 'video') {
    media = await uploadMemoryMedia({
      uid,
      memoryId: memoryRef.id,
      contributionId: contributionRef.id,
      type,
      file,
      thumbFile,
    });
  } else if (!String(text || '').trim()) {
    throw new Error('Write a short note.');
  }

  const payload = {
    authorUid: uid,
    title: String(title || '').trim(),
    memberUids: members,
    anniversaryMonth: now.getMonth() + 1,
    anniversaryDay: now.getDate(),
    coverThumbUrl: media.mediaThumbUrl || media.mediaUrl || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(memoryRef, payload);

  await setDoc(contributionRef, {
    authorUid: uid,
    type,
    text: String(text || '').trim(),
    ...media,
    createdAt: serverTimestamp(),
  });

  const invitees = members.filter((id) => id !== uid);
  if (invitees.length) {
    try {
      await createMemoryInviteNotifications({
        fromUid: uid,
        memoryId: memoryRef.id,
        memoryTitle: payload.title,
        toUids: invitees,
      });
    } catch (err) {
      console.warn('memory invite notifications failed:', err);
    }
  }

  return normalizeMemoryDoc(memoryRef.id, {
    ...payload,
    createdAt: now,
    updatedAt: now,
  });
}

export async function getMemory(memoryId) {
  if (!memoryId) return null;
  const snap = await getDoc(doc(db, MEMORIES_COLLECTION, memoryId));
  if (!snap.exists()) return null;
  return normalizeMemoryDoc(snap.id, snap.data());
}

export async function joinMemory(memoryId, uid) {
  if (!memoryId || !uid) throw new Error('Missing memory or user.');
  await updateDoc(doc(db, MEMORIES_COLLECTION, memoryId), {
    memberUids: arrayUnion(uid),
    updatedAt: serverTimestamp(),
  });
  return getMemory(memoryId);
}

export async function inviteToMemory(memoryId, uids = [], { fromUid = null, memoryTitle = '' } = {}) {
  if (!memoryId) throw new Error('Missing memory.');
  const ids = [...new Set((uids || []).filter(Boolean))];
  if (!ids.length) return getMemory(memoryId);
  await updateDoc(doc(db, MEMORIES_COLLECTION, memoryId), {
    memberUids: arrayUnion(...ids),
    updatedAt: serverTimestamp(),
  });
  const memory = await getMemory(memoryId);
  const inviter = fromUid || memory?.authorUid || null;
  if (inviter) {
    try {
      await createMemoryInviteNotifications({
        fromUid: inviter,
        memoryId,
        memoryTitle: memoryTitle || memory?.title || '',
        toUids: ids,
      });
    } catch (err) {
      console.warn('memory invite notifications failed:', err);
    }
  }
  return memory;
}

export async function addMemoryContribution({
  memoryId,
  uid,
  type = 'photo',
  text = '',
  file = null,
  thumbFile = null,
}) {
  if (!memoryId || !uid) throw new Error('Missing memory or user.');
  if (!MEMORY_TYPES.includes(type)) throw new Error('Invalid type.');
  const contributionRef = doc(collection(db, MEMORIES_COLLECTION, memoryId, 'contributions'));
  let media = {
    mediaUrl: null,
    mediaPath: null,
    mediaContentType: null,
    mediaThumbUrl: null,
    mediaThumbPath: null,
  };
  if (type === 'photo' || type === 'video') {
    media = await uploadMemoryMedia({
      uid,
      memoryId,
      contributionId: contributionRef.id,
      type,
      file,
      thumbFile,
    });
  } else if (!String(text || '').trim()) {
    throw new Error('Write a short note.');
  }
  await setDoc(contributionRef, {
    authorUid: uid,
    type,
    text: String(text || '').trim(),
    ...media,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, MEMORIES_COLLECTION, memoryId), {
    updatedAt: serverTimestamp(),
    ...(media.mediaThumbUrl || media.mediaUrl
      ? { coverThumbUrl: media.mediaThumbUrl || media.mediaUrl }
      : {}),
  });
  return normalizeContributionDoc(contributionRef.id, {
    authorUid: uid,
    type,
    text: String(text || '').trim(),
    ...media,
  });
}

export function subscribeMemoryContributions(memoryId, onData, onError) {
  if (!memoryId) {
    onData([]);
    return () => {};
  }
  const q = query(
    collection(db, MEMORIES_COLLECTION, memoryId, 'contributions'),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(
    q,
    (snap) => {
      onData(snap.docs.map((d) => normalizeContributionDoc(d.id, d.data())));
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}

/** Live memories where uid is a member. */
export function subscribeMemoriesForUser(uid, onData, onError) {
  if (!uid) {
    onData([]);
    return () => {};
  }
  const q = query(
    collection(db, MEMORIES_COLLECTION),
    where('memberUids', 'array-contains', uid)
  );
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => normalizeMemoryDoc(d.id, d.data()));
      list.sort((a, b) => {
        const at = toDate(a.createdAt)?.getTime() || 0;
        const bt = toDate(b.createdAt)?.getTime() || 0;
        return bt - at;
      });
      onData(list);
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}

export async function listOpenAnniversaryMemoriesForUser(uid, now = new Date()) {
  if (!uid) return [];
  const q = query(
    collection(db, MEMORIES_COLLECTION),
    where('memberUids', 'array-contains', uid)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => normalizeMemoryDoc(d.id, d.data()))
    .filter((m) => isMemoryOpen(m, now));
}

const REMINDER_KEY = 'm2b-memory-reminders-dismissed';

export function dismissedMemoryReminderKey(memoryId, year) {
  return `${memoryId}:${year}`;
}

export function loadDismissedMemoryReminders() {
  try {
    const raw = window.localStorage.getItem(REMINDER_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

export function dismissMemoryReminder(memoryId, year = new Date().getFullYear()) {
  try {
    const set = loadDismissedMemoryReminders();
    set.add(dismissedMemoryReminderKey(memoryId, year));
    window.localStorage.setItem(REMINDER_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export function shouldShowMemoryReminder(memory, now = new Date()) {
  if (!memory?.id || !isMemoryOpen(memory, now)) return false;
  const created = toDate(memory.createdAt);
  // Skip reminder during the first open day (just created).
  if (created && now.getTime() - created.getTime() < MEMORY_OPEN_MS) return false;
  const key = dismissedMemoryReminderKey(memory.id, now.getFullYear());
  return !loadDismissedMemoryReminders().has(key);
}
